import FilterForm from 'assets/components/filters/filterForm/FilterForm';
import ThemeFilterFormDrawer from 'assets/components/filters/filterForm/themes/Theme.FilterForm.Drawer';
import Icon from 'assets/components/icon/Icon';
import DateInput from 'assets/components/inputs/dateInput/DateInput';
import NumberInput from 'assets/components/inputs/number/NumberInput';
import SelectInput from 'assets/components/inputs/select/SelectInput';
import TextInput from 'assets/components/inputs/text/TextInput';
import useApi from 'assets/hooks/api/useApi';
import { getLocales } from 'assets/locales/Locale';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { defaultInputDateTimeFormat } from 'assets/utils/data/Date';
import { compact, flatten, isEmpty, uniqBy, values } from 'lodash';
import User from 'models/core/user/Model.User';
import Dealer from 'models/dealerManagement/dealer/Model.Dealer';
import SignedStatus from 'models/enums/SignedStatus';
import VertaforeStatuses from 'models/enums/VertaforeDocumentStatuses';
import VehicleModel from 'models/productManagement/vehicleModel/Model.VehicleModel';
import Distributor from 'models/sales/distributor/Model.Distributor';
import { useState } from 'react';
import { useSalesContractProvider } from '../Sales.Contract.Hooks';

export default function SalesContractFilters() {
  const { lang, translate } = getLocales();
  const { user, isDealer, isDistributor } = useAuthStore();
  const { listApi, permissions, canView, statuses } = useSalesContractProvider();
  const [signedStatus, setSignedStatus] = useState<Model.Enum.SignedStatus>(undefined);

  const dealersApi = useApi({
    action: Dealer.list,
    wait: true,
    body: {
      id: user?.dealers?.map((d) => d.id) ?? undefined,
      distributorId: user?.distributorId ? [user?.distributorId] : undefined,
    },
  });
  const distributorsApi = useApi({
    action: Distributor.list,
    wait: !isDealer && !isDistributor,
  });

  const userListApi = useApi({
    action: User.list,
    body: {
      isDisabled: false,
      distributorId: user?.distributorId ? [user?.distributorId] : undefined,
      dealerId: compact(user?.dealers?.map((it) => it.id)),
    },
    wait: !canView || !permissions.CREATE_CONTRACT,
  });

  const modelsApi = useApi({
    action: VehicleModel.list,
    wait: true,
  });

  return (
    <FilterForm
      subcomponents={ThemeFilterFormDrawer}
      filters={listApi.body}
      onFilter={(newFilers) => listApi.execute({ ...newFilers, offset: 0 })}
      onReset={() => {
        setSignedStatus(undefined);
        listApi.reset();
      }}
      onOpen={() => {
        if (!dealersApi.payload) dealersApi.execute((body) => body);
        if (!distributorsApi.payload) distributorsApi.execute((body) => body);
        if (!modelsApi.payload) modelsApi.execute((body) => body);
      }}
      render={({ tempFilters, setFilters }) => {
        const dealers = dealersApi?.payload?.data ?? [];
        const distributors = distributorsApi?.payload?.data ?? [];
        const users = userListApi?.payload?.data ?? [];

        const products = uniqBy(
          !isEmpty(tempFilters?.dealerId)
            ? flatten(dealers.filter((d) => tempFilters?.dealerId?.some((id) => id === d.id)).map((d) => d.products))
            : flatten(dealers.map((d) => d.products)),
          (p) => p.id
        );

        const makes =
          compact(
            uniqBy(
              modelsApi?.payload?.data?.map((m) => m.vehicleMake),
              (m) => m.id
            )
          ) ?? [];

        const models =
          modelsApi?.payload?.data?.filter((m) =>
            tempFilters?.makeCode?.some((code) => code === m.vehicleMake?.makeCode)
          ) ?? [];

        return (
          <>
            <TextInput
              name="contractNumber"
              label={lang.contractNumber}
              value={tempFilters?.contractNumber}
              onChange={setFilters}
              icon={<Icon class="if-quill-pen" />}
            />
            <TextInput
              name="vin"
              label={lang.vin}
              value={tempFilters?.vin}
              onChange={setFilters}
              icon={<Icon class="if-quill-pen" />}
            />

            <TextInput
              name="clientName"
              label={lang.clientName}
              value={tempFilters?.clientName}
              onChange={setFilters}
              icon={<Icon class="if-quill-pen" />}
            />
            <SelectInput
              name="dealerId"
              label={lang.dealers}
              data={dealers.map((item) => ({
                ...item.displayInfo,
                icon: <Icon class="fas-cloud" />,
              }))}
              isMultiselect
              value={tempFilters?.dealerId}
              disabled={!dealers.length}
              onChange={setFilters}
              icon={<Icon class="fas-cloud" />}
            />
            {!isDealer && !isDistributor && (
              <SelectInput
                name="distributorId"
                label={lang.distributors}
                data={distributors.map((item) => ({
                  ...item.displayInfo,
                  icon: <Icon class="fas-cloud" />,
                }))}
                isMultiselect
                value={tempFilters?.distributorId}
                disabled={!distributors.length}
                onChange={setFilters}
                icon={<Icon class="fas-cloud" />}
              />
            )}
            <SelectInput
              name="productId"
              label={lang.products}
              data={products.map((item) => ({
                ...item.displayInfo,
                icon: <Icon class="fas-box" />,
              }))}
              isMultiselect
              value={tempFilters?.productId}
              onChange={setFilters}
              icon={<Icon class="fas-box" />}
            />
            <SelectInput
              name="statusCode"
              label={lang.status}
              data={statuses.map((item) => ({
                id: item.id,
                title: item.title,
                icon: <Icon class="fas-check" />,
              }))}
              isMultiselect
              value={tempFilters?.statusCode}
              onChange={setFilters}
              icon={<Icon class="fas-cloud" />}
            />
            <SelectInput
              name="createdById"
              label={lang.createdBy}
              data={users?.map((user) => ({
                id: user.id,
                title: user.fullName,
                icon: <Icon class="fas-user" />,
              }))}
              isMultiselect
              value={tempFilters?.createdById}
              onChange={setFilters}
              icon={<Icon class="fas-user" />}
            />
            <DateInput
              name="effectiveFromDateUtc"
              label={lang.effectiveFromDate}
              dateTimeFormat={defaultInputDateTimeFormat}
              value={tempFilters?.effectiveFromDateUtc}
              onChange={setFilters}
              icon={<Icon class="fas-calendar-alt" />}
            />
            <DateInput
              name="effectiveToDateUtc"
              label={lang.effectiveToDate}
              dateTimeFormat={defaultInputDateTimeFormat}
              value={tempFilters?.effectiveToDateUtc}
              onChange={setFilters}
              icon={<Icon class="fas-calendar-alt" />}
            />
            <DateInput
              name="expiryFromDateUtc"
              label={lang.expiryFromDate}
              dateTimeFormat={defaultInputDateTimeFormat}
              value={tempFilters?.expiryFromDateUtc}
              onChange={setFilters}
              icon={<Icon class="fas-calendar-alt" />}
            />
            <DateInput
              name="expiryToDateUtc"
              label={lang.expiryToDate}
              dateTimeFormat={defaultInputDateTimeFormat}
              value={tempFilters?.expiryToDateUtc}
              onChange={setFilters}
              icon={<Icon class="fas-calendar-alt" />}
            />
            <DateInput
              name="createdAtFromDateUtc"
              label={lang.createdAtFromDate}
              dateTimeFormat={defaultInputDateTimeFormat}
              value={tempFilters?.createdAtFromDateUtc}
              onChange={setFilters}
              icon={<Icon class="fas-calendar-alt" />}
            />
            <DateInput
              name="createdAtToDateUtc"
              label={lang.createdAtToDate}
              dateTimeFormat={defaultInputDateTimeFormat}
              value={tempFilters?.createdAtToDateUtc}
              onChange={setFilters}
              icon={<Icon class="fas-calendar-alt" />}
            />
            <DateInput
              name="modifiedAtFromDateUtc"
              label={lang.modifiedAtFromDate}
              dateTimeFormat={defaultInputDateTimeFormat}
              value={tempFilters?.modifiedAtFromDateUtc}
              onChange={setFilters}
              icon={<Icon class="fas-calendar-alt" />}
            />
            <DateInput
              name="modifiedAtToDateUtc"
              label={lang.modifiedAtToDate}
              dateTimeFormat={defaultInputDateTimeFormat}
              value={tempFilters?.modifiedAtToDateUtc}
              onChange={setFilters}
              icon={<Icon class="fas-calendar-alt" />}
            />
            <SelectInput
              name="vertaforeStatus"
              label={lang.vertaforeStatus}
              data={Object.values(VertaforeStatuses).map((it) => ({
                id: it,
                title: translate(it),
                icon: <Icon class="fas-double-check" />,
              }))}
              isMultiselect
              value={tempFilters?.vertaforeStatus}
              onChange={setFilters}
              icon={<Icon class="fas-cloud" />}
            />
            <SelectInput
              name="signedStatus"
              label={lang.signingStatus}
              data={values(SignedStatus).map((it) => ({
                id: it,
                title: translate(it),
                icon: <Icon class="fas-double-check" />,
              }))}
              value={signedStatus}
              onChange={({ signedStatus }) => {
                const statusMap = {
                  [SignedStatus.signedManually]: 'isSignedManually',
                  [SignedStatus.signedWithDocuSign]: 'isSignedWithDocuSign',
                  [SignedStatus.signedWithSignFlow]: 'isSignedWithSignFlow',
                  [SignedStatus.sentForSigning]: 'isSentForSigning',
                  [SignedStatus.allSigned]: 'isSigned',
                };

                const updatedFilters = {};
                //! IMPORTANT: Need to clean all previous signing status filters, so set them to 'undefined' instead of 'false'.
                values(statusMap).forEach((statusKey) => {
                  updatedFilters[statusKey] = undefined;
                });
                //

                if (signedStatus && statusMap[signedStatus]) {
                  updatedFilters[statusMap[signedStatus]] = true;
                }

                setFilters(updatedFilters);
                setSignedStatus(signedStatus);
              }}
              icon={<Icon class="fas-cloud" />}
            />
            <SelectInput
              name="makeCode"
              label={lang.vehicleMake}
              data={makes.map((item) => ({
                id: item.makeCode,
                title: item.makeCode,
                icon: <Icon class="fas-cloud" />,
              }))}
              isMultiselect
              value={tempFilters?.makeCode}
              disabled={!makes.length}
              onChange={setFilters}
              icon={<Icon class="fas-cloud" />}
            />
            <SelectInput
              name="vehicleModelId"
              label={lang.vehicleModel}
              data={models.map((item) => ({
                ...item.displayInfo,
                icon: <Icon class="fas-cloud" />,
              }))}
              isMultiselect
              value={tempFilters?.vehicleModelId}
              disabled={!models.length}
              onChange={setFilters}
              icon={<Icon class="fas-cloud" />}
            />
            <NumberInput
              name="vehicleYear"
              label={lang.vehicleYear}
              value={tempFilters?.vehicleYear}
              onChange={setFilters}
              icon={<Icon class="if-quill-pen" />}
            />
            <TextInput
              name="vehicleStockNumber"
              label={lang.stockNumber}
              value={tempFilters?.vehicleStockNumber}
              onChange={setFilters}
              icon={<Icon class="if-quill-pen" />}
            />
          </>
        );
      }}
    />
  );
}
